import { Config, makeRequest } from "@paywithglide/glide-js";
import { logger } from "../logger";

export type OnrampCreateTransactionRequest = {
  customerId: string;
  cryptoAmount: string;
  paymentMethodType: "UPI" | "IMPS";
  depositAddress: string; // User's wallet address
};

export type UpiNote = {
  type: number;
  msg: string;
};

export type OnrampCreateTransactionResponse = {
  status: number;
  code: number;
  data: {
    transactionId: string;
    fromAmount: string;
    toAmount: string;
    rate: string;
    fiatPaymentInstructions: {
      type: "UPI" | "IMPS";
      upi: {
        vpa: string;
        name: string;
      };
      upiNotes?: UpiNote[]; // Additional notes/instructions for the user
      otp: string; // Transaction note - critical for payment matching
    };
  };
};

// Type for direct response format (sandbox mode)
export type OnrampTransactionData = OnrampCreateTransactionResponse["data"];

export async function createTransaction(
  glideConfig: Config,
  request: OnrampCreateTransactionRequest,
): Promise<OnrampTransactionData> {
  try {
    // Response can be either wrapped { status, code, data } or direct { transactionId, ... }
    const response = await makeRequest<
      OnrampCreateTransactionResponse | OnrampTransactionData
    >(glideConfig, {
      url: `/onramp/transaction`,
      method: "POST",
      body: request,
    });

    // Check if response is wrapped format (has status and code)
    if ("status" in response && "code" in response) {
      const wrappedResponse = response as OnrampCreateTransactionResponse;
      if (wrappedResponse.status !== 1 || wrappedResponse.code !== 200) {
        throw new Error(
          `Onramp transaction API error: status ${wrappedResponse.status}, code ${wrappedResponse.code}`,
        );
      }
      return wrappedResponse.data;
    }

    // Direct response format (sandbox mode) - response is the data itself
    if ("transactionId" in response) {
      return response as OnrampTransactionData;
    }

    // Unknown format
    throw new Error("Unexpected response format from Onramp transaction API");
  } catch (error) {
    logger.error(`Failed to create Onramp transaction: ${error}`);
    throw error;
  }
}
